import { FC, useEffect, useState } from 'react';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { showSystemToast } from 'view/components/SystemToast';
import { Button, CompanyAvatar, CompanyCard } from 'view/components';
import { Modal, Alert, Chip, CircularProgress, TextField, Tooltip, Box } from '@mui/material';
import { CloseIcon } from 'assets';
import * as CS from 'view/modals/styled';
import Autocomplete from 'view/components/Autocomplete';
import { useGetCompaniesForRecruiter } from 'view/pages/createJobPost/hooks/useGetCompaniesForRecruiter';
import { CreateCompanyModal } from 'view/pages/createJobPost/components/CreateCompanyModal';
import { MyChangeRequests } from 'view/pages/profile/components/MyChangeRequests';
import { Companies } from 'types';

export const CompanyMembershipManager: FC = () => {
  const [memberships, setMemberships] = useState<any[]>([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [joinCompany, setJoinCompany] = useState<any>(null);
  const [joinInputValue, setJoinInputValue] = useState('');
  const [joinPage, setJoinPage] = useState(1);
  const [joinError, setJoinError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const {
    data,
    send: fetchMemberships,
    isLoading,
  } = useRequest({
    url: '/api/v1/memberships/me',
    method: ApiMethodsEnum.Get,
    sendOnStart: true,
  });

  const { data: changeRequestsData, isLoading: isLoadingCR } = useRequest({
    url: '/api/v1/company-change-requests/me',
    method: ApiMethodsEnum.Get,
    sendOnStart: true,
  });

  const {
    send: sendVerification,
    isLoading: isSendingVerification,
    error: sendVerificationError,
  } = useRequest({
    url: `/api/v1/memberships/${selectedMembershipId}/send-verification`,
    method: ApiMethodsEnum.Post,
  });

  const { data: companiesData, isLoading: isLoadingCompanies } = useGetCompaniesForRecruiter(
    joinInputValue,
    joinPage
  );

  const { send: sendJoinMembership, isLoading: isJoining } = useRequest({
    url: '/api/v1/memberships',
    method: ApiMethodsEnum.Post,
  });

  useEffect(() => {
    if (data) {
      setMemberships(data);
    }
  }, [data]);

  useEffect(() => {
    if (sendVerificationError?.response?.data?.message) {
      setEmailError(sendVerificationError.response.data.message);
    }
  }, [sendVerificationError]);

  const handleSendVerification = async () => {
    setEmailError('');

    if (!workEmail.trim()) {
      setEmailError('Please enter your work email');
      return;
    }

    const result = await sendVerification({ email: workEmail });
    if (result) {
      showSystemToast('success', 'Verification email sent! Check your inbox.');
      setEmailDialogOpen(false);
      setWorkEmail('');
      fetchMemberships();
    }
  };

  const handleJoinCompany = async () => {
    if (!joinCompany) return;
    setJoinError('');
    const result = await sendJoinMembership({
      company: { id: joinCompany.id },
      role: 'INTERNAL_RECRUITER',
    });
    if (result) {
      showSystemToast('success', `Joined ${joinCompany.name}. Pending admin approval.`);
      setJoinCompany(null);
      setJoinInputValue('');
      fetchMemberships();
    }
  };

  const getStatusBadge = (membership: any) => {
    const status = membership.verificationStatus;

    switch (status) {
      case 'VERIFIED':
        return (
          <Chip
            label="Verified"
            size="small"
            sx={{
              backgroundColor: '#10b981',
              color: 'white',
              fontWeight: 600,
            }}
          />
        );
      case 'PENDING':
        return (
          <Chip
            label="Pending Approval"
            size="small"
            sx={{
              backgroundColor: '#f59e0b',
              color: 'white',
              fontWeight: 600,
            }}
          />
        );
      case 'REJECTED':
        return (
          <Chip
            label="Rejected"
            size="small"
            sx={{
              backgroundColor: '#ef4444',
              color: 'white',
              fontWeight: 600,
            }}
          />
        );
      default:
        return (
          <Chip
            label="Unverified"
            size="small"
            sx={{
              backgroundColor: '#6b7280',
              color: 'white',
              fontWeight: 600,
            }}
          />
        );
    }
  };

  const changeRequests: any[] = Array.isArray(changeRequestsData)
    ? changeRequestsData
    : changeRequestsData?.data || [];

  if (isLoading || isLoadingCR) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600 }}>Join a Company</h3>
        <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
          Search for an existing company to join. Your membership will require admin approval.
        </p>
        {joinError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {joinError}
          </Alert>
        )}
        <Autocomplete<Companies>
          label="Search for a company"
          value={joinCompany}
          onChange={(val: any) => {
            setJoinCompany(val);
            setJoinError('');
          }}
          inputValue={joinInputValue}
          setInputValue={setJoinInputValue}
          page={joinPage}
          setPage={setJoinPage}
          data={companiesData}
          isLoading={isLoadingCompanies}
          getOptionLabel={(option) => {
            return option.name || '';
          }}
          marginbottom={12}
          renderOption={(option) => {
            return (
              <Tooltip
                describeChild
                placement="right"
                // The tooltip surface has its own grey background and a 4px borderRadius, which
                // showed as dark corners behind the 16px card. Target it through popper: the
                // `& .MuiTooltip-tooltip` selector outweighs the surface's own styles.
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        width: '260px',
                        maxWidth: 'none',
                        padding: 0,
                        margin: 0,
                        backgroundColor: 'transparent',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        boxShadow:
                          '0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
                      },
                    },
                  },
                }}
                title={<CompanyCard company={option} />}
              >
                <Box sx={{ width: '100%' }}>{option.name}</Box>
              </Tooltip>
            );
          }}
        />
        <Button
          variant="contained"
          text={isJoining ? 'Joining...' : 'Join Company'}
          onClick={handleJoinCompany}
          disabled={!joinCompany || isJoining}
          style={{ minHeight: '40px' }}
        />
        <Button
          variant="outlined"
          text="Create New Company"
          onClick={() => {
            return setCreateModalOpen(true);
          }}
          style={{ minHeight: '40px', marginTop: '12px' }}
        />
      </div>

      {memberships.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          You don&apos;t have any company memberships yet. Join a company above or create a new one
          when posting a job.
        </Alert>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {memberships.map((membership) => {
            return (
              <div
                key={membership.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: 'white',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}
                >
                  <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
                    <CompanyAvatar company={membership.company} />
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
                        {membership.company?.name || 'Unknown Company'}
                      </h3>

                      <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                        Role: {membership.role?.replace(/_/g, ' ')}
                      </p>

                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          marginBottom: '12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {getStatusBadge(membership)}
                        {membership.workEmailVerified && (
                          <Chip
                            label="Email Verified"
                            size="small"
                            sx={{
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </div>

                      {membership.workEmail && (
                        <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '13px' }}>
                          Work Email: {membership.workEmail}
                        </p>
                      )}

                      {membership.rejectionReason && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          Rejection Reason: {membership.rejectionReason}
                        </Alert>
                      )}
                      {membership.verificationStatus === 'REJECTED' && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          You can join another company or create a new one using the form above.
                        </Alert>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(membership.verificationStatus === 'UNVERIFIED' ||
                      membership.verificationStatus === 'PENDING') &&
                      !membership.workEmailVerified && (
                        <Button
                          variant="outlined"
                          text="Verify Email"
                          onClick={() => {
                            setSelectedMembershipId(membership.id);
                            setSelectedCompanyName(membership.company?.name || '');
                            setWorkEmail(membership.workEmail || '');
                            setEmailError('');
                            setEmailDialogOpen(true);
                          }}
                          style={{ minHeight: '40px' }}
                        />
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateCompanyModal
        open={createModalOpen}
        onClose={() => {
          return setCreateModalOpen(false);
        }}
        onCreated={() => {
          fetchMemberships();
        }}
      />

      <MyChangeRequests changeRequests={changeRequests} />

      <Modal
        open={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false);
          setEmailError('');
        }}
        aria-labelledby="verify-email-modal"
      >
        <CS.ModalMainWrapper>
          <CS.CloseIconBox
            onClick={() => {
              setEmailDialogOpen(false);
              setEmailError('');
            }}
          >
            <CloseIcon />
          </CS.CloseIconBox>

          <CS.ModalMainHeader style={{ marginBottom: '24px' }}>
            <CS.ModalMainHeaderTitle>Verify Work Email</CS.ModalMainHeaderTitle>
            <CS.ModalMainHeaderSubTitle>
              Enter your work email for {selectedCompanyName} to verify your membership
            </CS.ModalMainHeaderSubTitle>
          </CS.ModalMainHeader>

          {emailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailError}
            </Alert>
          )}

          <TextField
            name="workEmail"
            placeholder="your.name@company.com"
            value={workEmail}
            onChange={(e: any) => {
              setWorkEmail(e.target.value);
              setEmailError('');
            }}
            fullWidth
            variant="outlined"
            type="email"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              },
            }}
          />

          <CS.ModalMainFooter>
            <Button
              variant="outlined"
              text="Cancel"
              onClick={() => {
                setEmailDialogOpen(false);
                setEmailError('');
              }}
              disabled={isSendingVerification}
              style={{ width: '100%', minHeight: '50px' }}
            />
            <Button
              variant="contained"
              text={isSendingVerification ? 'Sending...' : 'Send Verification Email'}
              onClick={handleSendVerification}
              disabled={isSendingVerification || !workEmail.trim()}
            />
          </CS.ModalMainFooter>
        </CS.ModalMainWrapper>
      </Modal>
    </div>
  );
};
